// UTILS
import { computed, ref } from "vue"
import { defineStore } from "pinia"
import { initMixpanel } from "@/plugins/mixpanel/mixpanel"
import { parseHTMLEntity } from "@/helpers/parse-entity"
import { useCookies } from "@vueuse/integrations/useCookies"
import { COOKIES } from "@/config/constants"
import Bugsnag from "@bugsnag/js"

// API
import { getMe } from "@/api/viktor/me"
import { patchUser } from "@/api/viktor/user/user"
import { getSharepools } from "@/api/viktor/user/sharepools"
import { getWorkflows } from "@/api/viktor/me/workflows"
import { getNextAssessment } from "@/api/viktor/assessment/next"
import { getAssessmentSummary } from "@/api/viktor/user/assessmentSummary"
import { getPoleEmploiSynthesis } from "@/api/viktor/user/poleEmploiSynthesis"

// TYPES
import type { NextAssessment } from "@/types/next-assessment"
import type { Workflow } from "@/types/workflows"
import type { AssessmentsSummary } from "@/types/assessments-summary"

// STORES
import { useI18nStore } from "@/stores/i18n"
import { useConfigStore } from "@/stores/config"

export const useUserStore = defineStore("user", () => {
  //
  // States
  //

  const user = ref()
  const stepper = ref()
  const workflows = ref<Workflow[]>()
  const nextAssessment = ref<NextAssessment | null>(null)
  const nextAssessmentName = ref<string | null>(null)
  const assessmentSummary = ref<AssessmentsSummary>()
  const assessmentPE = ref()
  const sharepools = ref()
  const audio = ref(false)

  const { currentLang } = useI18nStore()
  const cookies = useCookies()

  const isLoggedIn = computed(() => !!user.value)

  //
  // USER Getters
  //

  const getUserUuid = computed(() => {
    if (!user.value) return null
    return user.value.user.uuid
  })

  const getUserEmail = computed(() => {
    if (!user.value) return null
    return user.value.user.email
  })

  const getPermissions = computed(() => {
    if (!user.value) return []
    return user.value.permissions
  })

  // this user is using assessment with an iframe
  const isFromInt = computed(() => {
    if (!user.value) return false
    return user.value.permissions.includes("int")
  })

  const isGuestAcc = computed(() => {
    if (!user.value) return false
    return user.value.permissions.includes("gu_acc")
  })

  const isAdminOnly = computed(() => {
    if (!user.value) return false
    return (
      user.value.permissions.includes("cadm_r") &&
      !user.value.permissions.includes("rct_r")
    )
  })

  const isRecruiter = computed(() => {
    if (!user.value) return false
    return user.value.permissions.includes("rct_r")
  })

  const isManager = computed(() => {
    if (!user.value) return false
    return user.value.permissions.includes("mng_r")
  })

  const isPoleEmploi = computed(() => {
    if (!user.value) return false
    return user.value.is_pole_emploi
  })

  const getFullName = computed(() => {
    if (!user.value) return ""
    return parseHTMLEntity(
      `${user.value.user.first_name} ${user.value.user.last_name}`
    )
  })

  const getFirstName = computed(() => {
    if (!user.value) return ""
    return parseHTMLEntity(user.value.user.first_name)
  })

  const getProfileGender = computed(() => {
    if (!user.value) return undefined

    const gender =
      user.value.user.gender === "m"
        ? "male"
        : user.value.user.gender === "f"
          ? "female"
          : "other"

    return gender
  })

  const getShortGender = computed(() => {
    if (!user.value) return undefined
    return user.value.user.gender
  })

  const getVoiceUuid = computed(() => {
    if (!nextAssessment.value) return undefined
    return nextAssessment.value.next_assessment?.extra_data?.voice_user_uuid
  })

  //
  // USER Actions
  //

  async function fetchUser() {
    user.value = await getMe()
    // Redirect to terms of use if needed

    // We are using 2 cookies that should only exist for screening
    // $$ TODO: Remove this when we have a better way to handle pending flows
    const isFromPendingFlow =
      cookies.get(COOKIES.PM_UUID) && cookies.get(COOKIES.SCREENING_AGENT_HR)

    // Redirect to MFA if needed
    if (user.value?.permissions?.includes("mfa_req")) {
      const configStore = useConfigStore()
      return window.location.assign(`${configStore.urls.auth_url}/mfa`)
    }

    // Redirect to terms of use if needed
    if (user.value?.permissions?.includes("cst_na") && !isFromPendingFlow) {
      const configStore = useConfigStore()
      return window.location.assign(`${configStore.urls.auth_url}/terms-of-use`)
    }

    // setup user for mixpanel + bugsnag
    await initMixpanel(user.value.user.uuid, user.value.user.email, currentLang)
    Bugsnag.setUser(user.value.user.uuid, user.value.user.email, currentLang)

    return user.value
  }

  async function updateUser(data) {
    user.value.user = await patchUser(user.value.user.uuid, data)
    return user.value
  }

  async function setAudio(bool: boolean) {
    audio.value = bool
  }

  //
  // ASSESSMENTS Getters
  //

  async function fetchAssessmentSummary() {
    if (!user.value) {
      await fetchUser()
    }

    assessmentSummary.value = await getAssessmentSummary(user.value.user.uuid)
    return assessmentSummary.value
  }

  async function fetchPoleEmploiSynthesis() {
    if (!user.value) {
      await fetchUser()
    }

    assessmentPE.value = await getPoleEmploiSynthesis(user.value.user.uuid)
    return assessmentPE.value
  }

  async function fetchNextAssessment(
    pm_uuid?: string,
    company_uuid?: string,
    retake?: boolean
  ) {
    nextAssessment.value = await getNextAssessment(
      pm_uuid,
      company_uuid,
      retake
    )

    nextAssessmentName.value =
      nextAssessment.value?.next_assessment?.assessment ?? null

    return nextAssessment.value?.next_assessment ?? null
  }

  const nextAssessmentExtraData = computed(
    () => nextAssessment.value?.next_assessment?.extra_data
  )
  const getBrainStatus = computed(() => {
    if (!assessmentSummary.value) {
      return {}
    }

    return assessmentSummary.value.assessments["brain"].status
  })

  const getVoiceByUuid = computed(() => {
    return (voiceUuid: string) => {
      // Trouve la voice correspondante dans le tableau voices
      const voice = assessmentSummary.value?.assessments?.voices?.find(
        (v) => v.voice_uuid === voiceUuid
      )

      return voice ?? null
    }
  })

  //
  // WORKFLOWS Actions
  //

  async function fetchWorkflows() {
    if (!user.value) {
      await fetchUser()
    }

    const response = await getWorkflows()
    workflows.value = response.body
    return workflows.value
  }

  const isAllWorkflowsDone = computed(() => {
    if (!workflows.value) return true
    return workflows.value.every(
      (workflow: Workflow) => workflow.status === "completed"
    )
  })

  const isAllRequiredWorkflowsDone = computed(
    () =>
      workflows.value
        ?.filter((w) => !w.is_fake)
        .every((w) => w.status === "completed") ?? true
  )

  //
  // SHAREPOOLS Actions
  //

  async function fetchSharepools() {
    if (!user.value) {
      await fetchUser()
    }

    sharepools.value = await getSharepools()
    return sharepools.value
  }

  return {
    user,
    stepper,
    workflows,
    nextAssessment,
    nextAssessmentName,
    nextAssessmentExtraData,
    assessmentSummary,
    assessmentPE,
    audio,
    isLoggedIn,
    getUserUuid,
    getUserEmail,
    getPermissions,
    getFullName,
    getFirstName,
    getProfileGender,
    getShortGender,
    getVoiceUuid,
    getBrainStatus,
    getVoiceByUuid,
    fetchUser,
    updateUser,
    setAudio,
    fetchAssessmentSummary,
    fetchPoleEmploiSynthesis,
    fetchWorkflows,
    fetchNextAssessment,
    fetchSharepools,
    isAdminOnly,
    isRecruiter,
    isManager,
    isPoleEmploi,
    isAllWorkflowsDone,
    isAllRequiredWorkflowsDone,
    isFromInt,
    isGuestAcc,
    sharepools,
  }
})
